import {ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {QuantityNamePipe} from '../../../shared/shared.pipe';
import {Planning, QuantityType} from 'smag-commons/dto/msg';
import {EnergyCommunityNetwork, mkQuantityCoefficient, mkQuantityUom} from '../../../shared/entities';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {I18NService} from '../../../i18n/i18n.service';
import {takeUntil} from 'rxjs/operators';
import {SharedEnergyData} from '../../../shared/shared-energy-chart/shared-energy-chart.component';
import {AuthService} from 'auth-commons';
import {I18NTimestampPipe} from '../../../i18n/i18n.pipe';
import {TIME_DELTA_HOUR, TIME_DELTA_QUARTER_HOUR} from '../../../shared/line-chart/line-chart.pipe';
import * as _ from 'lodash';
import {EntityType} from 'smag-commons/cfg/hierarchy';
import {mkHourlyBins} from '../../ec-planning/ec-planning-util';
import {ResizeService} from '../../../shared/resize.service';


// quantities shown depend on entity's type
interface PlannedQuantity {
    type: QuantityType;
    name: string;
    data: [number, number][]; // planned data
    uom: string;
    color?: string;
    icon: string;
}

interface CommunityPlannedData {
    sharedEnergyData: SharedEnergyData[];
    quantities: PlannedQuantity[];
}

interface EnergyBin {
    baseTimestamp: number;
    timestamps: number[]; // sorted
    absorbedPowers: number[]; // corresponding to `timestamps`
    injectedPowers: number[]; // corresponding to `timestamps`
    sharedPowers: number[]; // corresponding to `timestamps`
    absorbedEnergy?: number;
    injectedEnergy?: number;
    sharedEnergy?: number;
}

interface PodPlannedQuantities {
    podQuantities: PlannedQuantity[];
    childQuantities: { [deviceId: string]: PlannedQuantity[] };
}

@Component({
    selector: 'app-community-planning-data',
    templateUrl: './community-planning-data.component.html',
    styleUrls: ['./community-planning-data.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityPlanningDataComponent implements OnInit, OnChanges, OnDestroy {

    private destroyed = new Subject<void>();
    private qnp: QuantityNamePipe;
    private tsp: I18NTimestampPipe;

    @Input() planning: Planning;
    @Input() network: EnergyCommunityNetwork;
    @Input() resourceId: Observable<string>;

    contentResize: Observable<void>;
    plannedCommunity: BehaviorSubject<CommunityPlannedData>;
    plannedPods: BehaviorSubject<{ [deviceId: string]: PodPlannedQuantities }>;

    TIME_DELTA_QUARTER_HOUR = TIME_DELTA_QUARTER_HOUR;
    TIME_DELTA_HOUR = TIME_DELTA_HOUR;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly i18n: I18NService,
                private readonly auth: AuthService,
                private readonly resizeService: ResizeService) {
        this.qnp = new QuantityNamePipe();
        this.tsp = new I18NTimestampPipe(i18n, auth);
        this.contentResize = this.resizeService.observe(['content']).pipe(
            takeUntil(this.destroyed)
        );
        this.planning = null;
        this.network = null;
        this.plannedCommunity = new BehaviorSubject({quantities: [], sharedEnergyData: null});
        this.plannedPods = new BehaviorSubject({});
    }

    ngOnInit() {
        this.resourceId.pipe(
            takeUntil(this.destroyed)
        ).subscribe(resourceId => {
            const type = this.network.entities[resourceId].type;
            const hasChildren = this.network.entities[resourceId].children && this.network.entities[resourceId].children.length > 0;
            const plannedData: CommunityPlannedData = {
                // quantities: this.mkPlannedQuantities(toScope(type), this.planning.data ? this.planning.data[this.network.root.id] || {} : {}),
                quantities: this.mkPlannedQuantities(toScope(type, hasChildren), this.planning.data ? this.planning.data[resourceId] || {} : {}),
                sharedEnergyData: type === EntityType.ENERGY_COMMUNITY ? this.mkSharedEnergyData() : null
            };
            this.plannedCommunity.next(plannedData);
        });
    }

    ngOnChanges(changes: SimpleChanges) {
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }


    private mkPlannedQuantities(scope: 'community' | 'pod' | 'plant' | 'accumulator' | 'prosumer',
                                data: { [qt in QuantityType]?: number[] }): PlannedQuantity[] {
        const qts = _.keys(data)
            .filter((qt: QuantityType) => mkQuantityWhiteList(scope).includes(qt))
            .map((qt: QuantityType) => ({
                type: qt,
                name: this.i18n.translate(this.qnp.transform(qt)),
                data: mkPlanningData(this.planning.timeframe, this.planning.end_time, data[qt], mkQuantityCoefficient(qt)),
                uom: mkQuantityUom(qt),
                icon: scope === 'community' ? mkCommunityIcon(qt) : '',
                color: scope === 'community' ? this.mkCommunityColor(qt) : this.cfg.shared.quantityColors[qt] || this.cfg.shared.defaultColor
            } as PlannedQuantity));
        if (scope === 'community') {
            return [
                qts.find(qt => qt.type === QuantityType.ABSORBED_POWER),
                qts.find(qt => qt.type === QuantityType.INJECTED_POWER),
                qts.find(qt => qt.type === QuantityType.GENERATED_POWER),
            ].filter(qt => !!qt);
        }
        return _.sortBy(qts, qt => qt.name);
    }

    private mkSharedEnergyData(): SharedEnergyData[] {
        const data = this.planning.data ? this.planning.data[this.network.root.id] || {} : {};
        return mkHourlyBins(this.planning.timeframe, data, this.planning.end_time).map(bin => ({
            time: bin.baseTimestamp,
            absorbedEnergy: bin.absorbedEnergy,
            injectedEnergy: bin.injectedEnergy,
            sharedEnergy: bin.sharedEnergy,
            opacity: 1,
            strokeWidth: 3
        }));
    }

    private mkCommunityColor(qt: QuantityType): string {
        switch (qt) {
            case QuantityType.SHARED_POWER:
                return this.cfg.shared.colors.sharedPower;
            case QuantityType.GENERATED_POWER:
                return this.cfg.shared.colors.generatedPower;
            case QuantityType.CONSUMED_POWER:
                return this.cfg.shared.colors.consumedPower;
            case QuantityType.INJECTED_POWER:
                return this.cfg.shared.colors.injectedPower;
            case QuantityType.ABSORBED_POWER:
                return this.cfg.shared.colors.absorbedPower;
            default:
                return this.cfg.shared.defaultColor;
        }
    }


    get gridColor(): string {
        return this.cfg.shared.gridColor;
    }

    get tooltipBorderColor(): string {
        return this.cfg.shared.tooltipBorderColor;
    }

    get tooltipBackgroundColor(): string {
        return this.cfg.shared.tooltipBackgroundColor;
    }

    get selectionLineColor(): string {
        return this.cfg.shared.selectionLineColor;
    }

    get injectedColor(): string {
        return this.cfg.shared.colors.injectedPower;
    }

    get absorbedColor(): string {
        return this.cfg.shared.colors.absorbedPower;
    }

    get sharedColor(): string {
        return this.cfg.shared.colors.sharedPower;
    }
}

function mkQuantityWhiteList(scope: 'community' | 'pod' | 'plant' | 'accumulator' | 'prosumer'): QuantityType[] {
    switch (scope) {
        case 'community':
            return [QuantityType.ABSORBED_POWER, QuantityType.INJECTED_POWER, QuantityType.GENERATED_POWER];
        case 'pod':
            return [QuantityType.ABSORBED_POWER];
        case 'prosumer':
            return [QuantityType.ABSORBED_POWER, QuantityType.INJECTED_POWER, QuantityType.GENERATED_POWER];
        case 'plant':
            return [QuantityType.GENERATED_POWER];
        case 'accumulator':
            return [QuantityType.ACTIVE_POWER, QuantityType.STATE_OF_CHARGE];
        default:
            return [];
    }
}

function mkPlanningData(timeframe: number[], endTime: number, rawData: number[], coeff: number): [number, number][] {
    if (!rawData || rawData.length !== timeframe.length) {
        return [];
    }
    const data: [number, number][] = [];
    const tf = timeframe;
    for (let i = 0; i < tf.length; ++i) {
        // NOTE: every planned value corresponding to a particular time within the time-frame is meant to be
        //       a device's setpoint from that time to next time-frame's time, so a "stair-like" plot is used
        const point = rawData[i] * coeff;
        data.push([tf[i], point]);
        data.push([i < tf.length - 1 ? tf[i + 1] : endTime, point]);
    }
    return data;
}

function toScope(type: EntityType, hasChildren: boolean): 'community' | 'pod' | 'plant' | 'accumulator' | 'prosumer' {
    switch (type) {
        case EntityType.ENERGY_COMMUNITY:
            return 'community';
        case EntityType.POD:
            return hasChildren ? 'prosumer' : 'pod';
        case EntityType.PLANT:
            return 'plant';
        case EntityType.ACCUMULATOR:
            return 'accumulator';
        default:
            return 'community';
    }
}

function mkCommunityIcon(type: QuantityType): string {
    switch (type) {
        case QuantityType.ABSORBED_POWER:
            return 'fal-paper-plane';
        case QuantityType.INJECTED_POWER:
            return 'fal-paper-plane';
        case QuantityType.GENERATED_POWER:
            return 'fal-solar-panel';
        default:
            return 'fal-paper-plane';
    }
}
